/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer/auth`;
const api = axios.create({ baseURL: API, withCredentials: true });

const Ctx = createContext(null);

export function formatApiError(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function describeAxiosError(err, fallback = "Something went wrong. Please try again.") {
  // Network error — no response from the server.
  if (err && err.code === "ERR_NETWORK") {
    return "Couldn’t reach the server. Please check your connection and try again.";
  }
  if (err && !err.response) {
    return fallback;
  }
  const status = err.response.status;
  const data = err.response.data;
  const fromDetail = data && typeof data === "object" ? formatApiError(data.detail) : "";
  if (fromDetail) return fromDetail;
  if (status === 429) return "Too many attempts. Please wait a moment and try again.";
  if (status === 500) return "The server hit an unexpected error. Please try again shortly.";
  if (status >= 502 && status <= 504) return "The service is briefly unavailable. Please try again in a minute.";
  return `${fallback} (HTTP ${status})`;
}

export function CustomerAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=signed-in

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/register", payload);
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const { data } = await api.post("/verify-email", { token });
    setUser(data.user);
    return data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const { data } = await api.post("/forgot-password", { email });
    return data;
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    const { data } = await api.post("/reset-password", { token, password });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/logout");
    } catch (err) {
      // Best-effort: even if the server call fails, drop local session state.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("logout server call failed:", err?.message || err);
      }
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, register, login, verifyEmail, forgotPassword, resetPassword, logout, refresh, api }),
    [user, register, login, verifyEmail, forgotPassword, resetPassword, logout, refresh]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCustomerAuth() { return useContext(Ctx); }
